import { Controller } from "stimulus"

// Mounts Stripe Card Element on the inline payment page rendered after
// cart submission for "Płatności kartą Stripe". The Card Element is an
// iframe under js.stripe.com - the customer types their PAN there, our
// JS never sees it. On submit we call stripe.confirmCardPayment with
// the PaymentIntent client_secret (created by
// Stripe::CreateCardPaymentIntent during StripeCardPayment#register_transaction).
// If the issuer demands 3DS, confirmCardPayment pops the challenge
// modal itself - we just await the promise. On success Stripe redirects
// us back to the return_url; the PaymentIntent webhook fires
// independently and triggers StripeCardPayments::VerificationService
// to persist the StripeCardToken.

export default class extends Controller {
  static values = {
    clientSecret: String,
    publicKey: String,
    returnUrl: String
  }

  static targets = ["cardElement", "submitButton", "errorMessage", "cardholderName"]

  async connect() {
    if (typeof Stripe === "undefined") {
      this.showError("Nie udało się załadować Stripe. Odśwież stronę.")
      return
    }

    this.stripe = Stripe(this.publicKeyValue)
    this.elements = this.stripe.elements()
    this.cardElement = this.elements.create("card", {
      style: {
        base: {
          fontSize: "16px",
          color: "#222",
          fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          "::placeholder": { color: "#999" }
        },
        invalid: { color: "#c00" }
      }
    })
    this.cardElement.mount(this.cardElementTarget)
    this.cardElement.on("change", (event) => {
      if (event.error) {
        this.showError(event.error.message)
      } else {
        this.clearError()
      }
    })
  }

  async submit(event) {
    event.preventDefault()
    if (!this.stripe || !this.cardElement) return

    this.submitButtonTarget.disabled = true
    this.clearError()

    const { paymentIntent, error } = await this.stripe.confirmCardPayment(
      this.clientSecretValue,
      {
        payment_method: {
          card: this.cardElement,
          billing_details: {
            name: this.cardholderNameTarget?.value || ""
          }
        }
      }
    )

    if (error) {
      this.showError(error.message || "Płatność nie powiodła się. Sprawdź dane karty.")
      this.submitButtonTarget.disabled = false
      return
    }

    // succeeded or requires_capture (we use confirm: false later for
    // recurring; here on the inline form intent is auto-confirmed).
    // The PaymentIntent webhook fires asynchronously to persist
    // StripeCardToken; we just redirect to the return page.
    if (paymentIntent.status === "succeeded" || paymentIntent.status === "processing") {
      window.location.href = this.returnUrlValue
    } else {
      this.showError(`Stan płatności: ${paymentIntent.status}. Skontaktuj się ze sklepem.`)
      this.submitButtonTarget.disabled = false
    }
  }

  showError(message) {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = message
      this.errorMessageTarget.style.display = "block"
    }
  }

  clearError() {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = ""
      this.errorMessageTarget.style.display = "none"
    }
  }
}
